import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import {
	type IncomingInvoiceApprovalHandoverSchema,
	incomingInvoiceApprovalHandoverSchema,
} from "@msuite/utils/schemas";
import type { IncomingInvoiceDetail } from "@msuite/utils/types";
import { FormProvider, useForm } from "react-hook-form";
import { IncomingInvoiceApprovalHandoverForm } from "@/forms/incoming-invoice-approval-handover-form";
import { client } from "@/hono";

/** Props Interface */
interface HandOverApprovalModalProps extends ModalProps {
	incomingInvoice: IncomingInvoiceDetail;
	/** Only a default approver hands the invoice over as its sole approval. */
	canHandOverSole: boolean;
}

export function HandOverApprovalModal({
	isOpen,
	onClose,
	incomingInvoice,
	canHandOverSole,
}: HandOverApprovalModalProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();
	const methods = useForm<IncomingInvoiceApprovalHandoverSchema>({
		defaultValues: { userId: "", isSoleApprover: false },
		resolver: resolver(incomingInvoiceApprovalHandoverSchema),
	});

	/** Functions */
	async function handleOnSubmit(json: IncomingInvoiceApprovalHandoverSchema) {
		const response = await client.incoming_invoices[":incomingInvoiceId"].approvals.$post({
			param: { incomingInvoiceId: incomingInvoice.id },
			json,
		});
		await responseHandler(response);
		await qc.invalidateQueries({
			predicate: invalidate(["incoming-invoices", "incoming-invoice-approvals-mine"]),
		});
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("incoming_invoices.handover.hand_over")}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading}
			>
				<Modal.Body>
					<IncomingInvoiceApprovalHandoverForm
						incomingInvoiceId={incomingInvoice.id}
						canHandOverSole={canHandOverSole}
					/>
				</Modal.Body>
				<Modal.FooterForm submitLabel={t("incoming_invoices.handover.hand_over")} />
			</Modal>
		</FormProvider>
	);
}
